import { useEffect, useRef, useState } from "react";

interface UseInViewOptions extends IntersectionObserverInit {
  triggerOnce?: boolean;
}

/**
 * A custom React hook that detects when an element is in the viewport and provides visibility status.
 *
 * @param options - An optional object to customize the Intersection Observer behavior.
 * @returns A tuple containing:
 * - A React ref object to attach to the target element.
 * - A boolean indicating whether the element has been seen (intersected with the viewport) at least once.
 * - A boolean indicating whether the element is currently in the viewport.
 *
 * @remarks
 * The hook uses the Intersection Observer API to detect when the target element intersects with the viewport.
 * By default, the element is considered in view when it is at least 10% visible within the viewport.
 * The `options` parameter can be used to customize the Intersection Observer behavior, such as the threshold and root margin.
 *
 * @example
 * ```typescript
 * import React from 'react';
 * import { useInView } from './useInView';
 *
 * const MyComponent = () => {
 *   const [ref, isVisible, isInView] = useInView({ threshold: 0.5 });
 *
 *   return (
 *     <div ref={ref}>
 *       {isVisible && <p>Element has been seen at least once.</p>}
 *       {isInView && <p>Element is currently in view.</p>}
 *     </div>
 *   );
 * };
 * ```
 */
export const useInView = (options: UseInViewOptions = {}) => {
  const [isInView, setIsInView] = useState(false);
  const [hasBeenSeen, setHasBeenSeen] = useState(false);
  const elementRef = useRef(null);

  const defaultOptions = {
    threshold: 0.1,
    rootMargin: "10px", // creates a 10px buffer around the viewport
    triggerOnce: true,
    ...options,
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        const isIntersecting = entry.isIntersecting;
        setIsInView(isIntersecting);

        if (isIntersecting && !hasBeenSeen) {
          setHasBeenSeen(true);
        }
      },
      {
        threshold: defaultOptions.threshold,
        rootMargin: defaultOptions.rootMargin,
      },
    );

    const currentElement = elementRef.current;
    if (currentElement) {
      observer.observe(currentElement);
    }

    return () => {
      if (currentElement) {
        observer.unobserve(currentElement);
      }
    };
  }, [defaultOptions.threshold, defaultOptions.rootMargin, hasBeenSeen]);

  const shouldTrigger = defaultOptions.triggerOnce ? hasBeenSeen : isInView;
  return [elementRef, shouldTrigger, isInView] as const;
};
